<template>
  <div>
    <v-app-bar flat color="red-darken-3">
      <v-app-bar-title class="hidden-sm-and-down">
        {{ $t("support_tickets_docs.label") }}
      </v-app-bar-title>
      <v-divider class="mx-5 hidden-sm-and-down" inset vertical />
    </v-app-bar>

    <div class="pa-4">
      <v-card max-width="760" variant="flat">
        <v-card-text>
          <v-alert
            v-if="alert.value"
            :type="alert.type"
            class="mb-4"
            closable
            @click:close="alert.value = false"
          >
            {{ alert.message }}
          </v-alert>

          <v-text-field
            :model-value="subject"
            :label="$t('support_tickets_docs.subject')"
            :hint="$t('support_tickets_docs.subject_hint')"
            persistent-hint
            variant="solo"
            color="red-darken-1"
            maxlength="50"
            counter="50"
            :disabled="submitting"
            @update:model-value="onSubjectInput"
          />

          <!-- Help topic / department: restore when OSTICKET_DB_* is configured.
          <v-select
            v-model="topicId"
            :items="helpTopics"
            item-title="name"
            item-value="id"
            :label="$t('support_tickets_docs.help_topic')"
            :placeholder="$t('support_tickets_docs.help_topic_placeholder')"
            :loading="loadingOptions"
            variant="solo"
            color="red-darken-1"
            :disabled="submitting || loadingOptions"
          />

          <v-select
            v-model="deptId"
            :items="departments"
            item-title="name"
            item-value="id"
            :label="$t('support_tickets_docs.department')"
            :loading="loadingOptions"
            variant="solo"
            color="red-darken-1"
            :disabled="submitting || loadingOptions"
          />
          -->

          <v-textarea
            v-model="message"
            :label="$t('support_tickets_docs.description')"
            variant="solo"
            color="red-darken-1"
            rows="8"
            auto-grow
            :disabled="submitting"
          />

          <v-file-input
            v-model="screenshots"
            :label="$t('support_tickets_docs.screenshots')"
            :hint="$t('support_tickets_docs.screenshots_hint')"
            persistent-hint
            accept="image/png,image/jpeg,image/gif,image/webp,.png,.jpg,.jpeg,.gif,.webp"
            prepend-icon="mdi-image-multiple-outline"
            variant="solo"
            color="red-darken-1"
            multiple
            show-size
            counter
            :disabled="submitting"
            class="mt-4"
          />

          <v-file-input
            v-model="file"
            :label="$t('support_tickets_docs.file')"
            :hint="$t('support_tickets_docs.file_hint')"
            persistent-hint
            accept=".pdf,.txt,.csv,.zip,.xls,.xlsx,.docx,application/pdf,text/plain,text/csv,application/zip"
            prepend-icon="mdi-paperclip"
            variant="solo"
            color="red-darken-1"
            show-size
            :disabled="submitting"
            class="mt-4"
          />

          <v-btn
            class="mt-6"
            color="red-darken-4"
            :loading="submitting"
            :disabled="!canSubmit"
            @click="createTicket"
          >
            {{ $t("support_tickets_docs.create") }}
          </v-btn>
        </v-card-text>
      </v-card>

      <v-card class="mt-6" variant="flat">
        <v-card-title class="text-h6">
          {{ $t("support_tickets_docs.list_title") }}
        </v-card-title>
        <v-card-text>
          <v-data-table
            :headers="listHeaders"
            :items="records"
            :loading="loadingRecords"
            item-value="_id"
            density="compact"
            hover
          >
            <template #item.createdAt="{ item }">
              {{ formatDate(item.createdAt) }}
            </template>
            <template #no-data>
              <div class="pa-4 text-medium-emphasis">
                {{ $t("support_tickets_docs.list_empty") }}
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script>
import postRequest from "../api/postRequest";
import getRequest from "../api/getRequest";

const MAX_SCREENSHOTS = 5;
const MAX_FILE_SIZE = 5 * 1024 * 1024;

export default {
  name: "SupportTicketsDocs",
  data() {
    return {
      subject: "",
      message: "",
      topicId: null,
      deptId: null,
      helpTopics: [],
      departments: [],
      loadingOptions: false,
      screenshots: [],
      file: null,
      submitting: false,
      alert: { value: false, type: "success", message: "" },
      records: [],
      loadingRecords: false,
    };
  },
  computed: {
    screenshotList() {
      if (!this.screenshots) return [];
      return Array.isArray(this.screenshots) ? this.screenshots : [this.screenshots];
    },
    attachmentFiles() {
      const files = [...this.screenshotList];
      if (this.file) files.push(this.file);
      return files.filter(Boolean);
    },
    canSubmit() {
      return Boolean(this.subject.trim() && this.message.trim() && !this.submitting);
    },
    isLekoStaff() {
      const role = this.$store.getters.role;
      return role === "SuperAdmin" || role === "LekoAdmin";
    },
    isInstitutionAdmin() {
      return this.$store.getters.role === "InstitutionAdmin";
    },
    listHeaders() {
      const headers = [
        { title: this.$t("support_tickets_docs.col_number"), key: "ticketNumber" },
        { title: this.$t("support_tickets_docs.col_subject"), key: "subject" },
      ];
      if (this.isInstitutionAdmin || this.isLekoStaff) {
        headers.push({ title: this.$t("support_tickets_docs.col_user"), key: "createdByName" });
        headers.push({ title: this.$t("support_tickets_docs.col_email"), key: "createdByEmail" });
      }
      if (this.isLekoStaff) {
        headers.push({ title: this.$t("support_tickets_docs.col_institution"), key: "institutionName" });
      }
      headers.push({ title: this.$t("support_tickets_docs.col_date"), key: "createdAt" });
      return headers;
    },
  },
  created() {
    this.loadRecords();
  },
  methods: {
    // defaultDepartmentId(departments) {
    //   const itDept = departments.find((dept) => String(dept.name).trim().toLowerCase() === "it");
    //   return itDept ? itDept.id : (departments[0] && departments[0].id) || null;
    // },
    // async loadOptions() {
    //   this.loadingOptions = true;
    //   try {
    //     const [topicsRes, deptsRes] = await Promise.all([
    //       getRequest("/ticket/help-topics"),
    //       getRequest("/ticket/departments"),
    //     ]);
    //     this.helpTopics = (topicsRes && topicsRes.helpTopics) || [];
    //     this.departments = (deptsRes && deptsRes.departments) || [];
    //     if (!this.deptId) this.deptId = this.defaultDepartmentId(this.departments);
    //   } catch (error) {
    //     this.showAlert("error", this.$t("support_tickets_docs.error_options"));
    //   } finally {
    //     this.loadingOptions = false;
    //   }
    // },
    showAlert(type, message) {
      this.alert = { value: true, type, message };
    },
    onSubjectInput(value) {
      this.subject = String(value || "")
        .replace(/[^\p{L}\p{N} ]/gu, "")
        .slice(0, 50);
    },
    formatDate(value) {
      if (!value) return "";
      return new Date(value).toLocaleString();
    },
    async loadRecords() {
      this.loadingRecords = true;
      try {
        const data = await getRequest("/ticket/records");
        this.records = (data && data.records) || [];
      } catch (error) {
        this.records = [];
      } finally {
        this.loadingRecords = false;
      }
    },
    validateAttachments() {
      if (this.screenshotList.length > MAX_SCREENSHOTS) {
        this.showAlert("error", this.$t("support_tickets_docs.error_too_many_screenshots"));
        return false;
      }
      const oversized = this.attachmentFiles.find((item) => item.size > MAX_FILE_SIZE);
      if (oversized) {
        this.showAlert("error", this.$t("support_tickets_docs.error_file_too_large"));
        return false;
      }
      return true;
    },
    async createTicket() {
      if (!this.canSubmit) return;
      if (!this.validateAttachments()) return;

      this.submitting = true;
      this.alert.value = false;

      const formData = new FormData();
      formData.append("subject", this.subject.trim());
      formData.append("message", this.message.trim());
      // formData.append("topicId", this.topicId);
      // formData.append("deptId", this.deptId);
      this.attachmentFiles.forEach((item) => {
        formData.append("attachments", item);
      });

      try {
        const response = await postRequest.formData("/ticket", "", formData);
        const number = response.data && response.data.number;
        this.showAlert(
          "success",
          this.$t("support_tickets_docs.success", { number: number || "" }),
        );
        this.subject = "";
        this.message = "";
        // this.topicId = null;
        // this.deptId = this.defaultDepartmentId(this.departments);
        this.screenshots = [];
        this.file = null;
        this.loadRecords();
      } catch (error) {
        const serverMessage = error.response && error.response.data;
        this.showAlert(
          "error",
          typeof serverMessage === "string" && serverMessage
            ? serverMessage
            : this.$t("support_tickets_docs.error"),
        );
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
